import React, { useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { PhotoTableCellContent } from '@components/common/PhotoTableCellContent'
import { useMediaQueryContext } from '@fourthwall/components'
import { Offer } from '@fourthwall/services/dist/models/offer'
import { useOffersQuery } from '@queries/useOffersQuery'
import { getProductThumbnailType } from '@utils/products'

import { useSamplesInfiniteOffers } from '../../../../../hooks/useSamplesInfiniteOffers'
import type { FormValues } from '../../../SamplesOrders.types'
import type { Item, SelectedItem } from '../SelectItems.types'
import { getInitialSelectedItems } from '../utils/getInitialSelectedItems'
import { getItemsFromOffers } from '../utils/getItemsFromOffers'

export const useSelectItems = ({
  initialValues: providedInitialValues,
}: {
  initialValues: FormValues
}) => {
  const media = useMediaQueryContext()
  const [searchValue, setSearchValue] = useState('')
  const [searchValueDebounced] = useDebounce(searchValue, 300)
  const { samplesOffers } = useSamplesInfiniteOffers(searchValueDebounced)

  const { offersQuery: notAvailableOffersQuery } = useOffersQuery([
    {
      size: 1000,
      status: Offer.OFFER_STATUSES_WITH_SAMPLES,
      fulfillmentService: Offer.FULFILLING_SERVICES.filter(
        (item) => !Offer.FULFILLING_SERVICES_WITH_SAMPLES.includes(item),
      ),
      includeGiftCardProducts: true,
      includeBundleProducts: true,
    },
  ])

  const resetSearchValue = () => setSearchValue('')

  const getBanner = () => {
    if (!notAvailableOffersQuery.data) return undefined

    if (!notAvailableOffersQuery.data.offers.length) return undefined

    return {
      size: 'small' as const,
      title: `${notAvailableOffersQuery.data.offers.length} of your products are not available to order as samples.`,
      panel: (
        <div style={{ display: 'grid', gap: 4 }}>
          {notAvailableOffersQuery.data.offers.map((offer) => (
            <div>
              <PhotoTableCellContent
                name={offer.name}
                type={getProductThumbnailType(offer.offerType)}
                photo={offer.primaryImage?.thumbnail.url}
                photoSize={media.tablet ? 'medium' : 'tiny'}
              />
            </div>
          ))}
        </div>
      ),
    }
  }

  const items: Item[] = useMemo(() => {
    return getItemsFromOffers(
      samplesOffers.offers,
      notAvailableOffersQuery.data?.offers.map((offer) => offer.id) ?? [],
    )
  }, [samplesOffers.offers, notAvailableOffersQuery.data?.offers])

  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>(
    getInitialSelectedItems(providedInitialValues, items),
  )

  const hasErrors = selectedItems.some(({ error }) => error)

  return {
    banner: getBanner(),
    selection: {
      items,
      selectedItems,
      isLoading: samplesOffers.isLoading || notAvailableOffersQuery.isLoading,
      offersCount: samplesOffers.offers.length,
      hasNextPage: samplesOffers.hasNextPage,
      hasErrors,
      setSelectedItems,
      loadMoreItems: samplesOffers.fetchNextPage,
    },
    search: {
      value: searchValue,
      debouncedValue: searchValueDebounced,
      resetValue: resetSearchValue,
      setValue: setSearchValue,
    },
  }
}
